import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';
import { PublicPage, APP_NAME } from '@/components/public-page';
import { IS_HOOFDNAARHART } from '@/lib/constants';

interface LegalDocumentPageProps {
  /** The row's name in legal_documents -- these are edited in the admin screens. */
  documentName: 'Privacy Beleid' | 'Algemene Voorwaarden';
  title: string;
  description: string;
}

/**
 * One legal document, readable without signing in.
 *
 * Each app has its own copy of every document, told apart by role_scope: null
 * for Hartzorg, 'hoofdhartuser' for HoofdnaarHart. A visitor has no role to
 * match against, so the variant of the build decides which copy is shown --
 * the same split the two login pages already make. That is also why
 * legal_documents stays readable without a session while the other content
 * tables do not.
 */
export default function LegalDocumentPage({
  documentName,
  title,
  description,
}: LegalDocumentPageProps) {
  const [content, setContent] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      let query = supabase
        .from('legal_documents')
        .select('content, updated_at')
        .eq('name', documentName);

      query = IS_HOOFDNAARHART
        ? query.eq('role_scope', 'hoofdhartuser')
        : query.is('role_scope', null);

      const { data, error } = await query.maybeSingle();
      if (error) console.error(`Error fetching ${documentName}:`, error);
      setContent(data?.content ?? null);
      setUpdatedAt(data?.updated_at ?? null);
      setLoading(false);
    };

    fetchDocument();
  }, [documentName]);

  const subtitle = updatedAt
    ? `Laatst bijgewerkt: ${new Date(updatedAt).toLocaleDateString('nl-NL')}`
    : undefined;

  return (
    <PublicPage title={title} description={description} subtitle={subtitle}>
      {loading ? (
        <p className="py-4 text-center text-muted-foreground">Laden…</p>
      ) : content ? (
        <RichTextViewer content={content} />
      ) : (
        // Never leave a store reviewer on a blank page: say where to ask.
        <p className="text-muted-foreground">
          Dit document is op dit moment niet beschikbaar. Vraag het op via{' '}
          <Link className="underline" to="/support">
            support
          </Link>{' '}
          en we sturen het toe. ({APP_NAME})
        </p>
      )}
    </PublicPage>
  );
}
